type AudioEvent = 'ui' | 'shoot' | 'enemyShoot' | 'hit' | 'explode' | 'pickup' | 'shield' | 'wave' | 'win' | 'lose';

/** 声源世界坐标（XZ 平面）；省略 = 非空间音（UI/结算等恒全音量居中）。 */
export interface AudioPosition {
  x: number;
  z: number;
}

/** 距离衰减：该半径内全音量。 */
const NEAR_RADIUS = 7;
/** 衰减带宽度：NEAR_RADIUS 之外每单位线性降低，直到 MIN_GAIN 触底。 */
const FALLOFF_RANGE = 38;
/** 远距声音的音量下限（不完全静音——迷雾中的远处炮声仍可"听声辨位"）。 */
const MIN_GAIN = 0.12;
/** 声像映射：listener 左右 PAN_RANGE 单位处达到最大偏转。 */
const PAN_RANGE = 20;
/** 声像最大偏转（不打满 ±1，保持双耳都有信号）。 */
const PAN_LIMIT = 0.8;

export class AudioManager {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private readonly noiseBuffers = new Map<number, AudioBuffer>();
  private readonly lastPlayed = new Map<AudioEvent, number>();
  /** 听者世界坐标（TankGame 每帧用相机焦点刷新）。 */
  private listenerX = 0;
  private listenerZ = 0;
  /** 本次 play 调用解析出的空间参数（tone/noise 消费，调用间复位）。 */
  private spatialGain = 1;
  private spatialPan = 0;
  enabled = true;

  async unlock(): Promise<void> {
    if (!this.context) {
      const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) return;
      this.context = new Ctx();
      this.master = this.context.createGain();
      this.master.gain.value = 0.22;
      this.master.connect(this.context.destination);
      for (const duration of [0.055, 0.09, 0.32]) this.getNoiseBuffer(duration);
    }
    if (this.context.state === 'suspended') await this.context.resume();
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (this.master && this.context) {
      this.master.gain.cancelScheduledValues(this.context.currentTime);
      this.master.gain.setTargetAtTime(enabled ? 0.22 : 0.0001, this.context.currentTime, 0.02);
    }
  }

  toggle(): boolean {
    this.setEnabled(!this.enabled);
    return this.enabled;
  }

  /** 更新听者位置（相机焦点/本方坦克；空间音按此计算衰减与声像）。 */
  setListener(x: number, z: number): void {
    this.listenerX = x;
    this.listenerZ = z;
  }

  /**
   * 播放事件音效。
   *
   * @param at 声源世界坐标：给定时按距听者的距离做线性衰减（NEAR_RADIUS 内全音量，
   *           MIN_GAIN 触底不完全静音）并映射立体声声像——迷雾中看不见的敌人开火/爆炸
   *           可凭音量与左右方位判断（设计文档 §6「听声辨位」，2026-07-26 落地）。
   *           省略 = 非空间音（UI/横幅/结算），恒全音量居中。
   */
  play(event: AudioEvent, variation = 0, at?: AudioPosition | null): void {
    if (!this.enabled || !this.context || !this.master || this.context.state !== 'running') return;
    const now = this.context.currentTime;
    const minimumGap = event === 'enemyShoot' ? 0.035 : event === 'hit' ? 0.025 : 0;
    const last = this.lastPlayed.get(event) ?? -Infinity;
    if (minimumGap > 0 && now - last < minimumGap) return;
    this.lastPlayed.set(event, now);
    if (at) {
      const dx = at.x - this.listenerX;
      const dz = at.z - this.listenerZ;
      const distance = Math.hypot(dx, dz);
      this.spatialGain = distance <= NEAR_RADIUS ? 1 : Math.max(MIN_GAIN, 1 - (distance - NEAR_RADIUS) / FALLOFF_RANGE);
      this.spatialPan = Math.max(-PAN_LIMIT, Math.min(PAN_LIMIT, dx / PAN_RANGE));
    } else {
      this.spatialGain = 1;
      this.spatialPan = 0;
    }
    switch (event) {
      case 'ui':
        this.tone(520 + variation * 20, 760, 0.075, 'square', 0.035, now);
        break;
      case 'shoot':
        this.tone(170 + variation * 8, 70, 0.13, 'sawtooth', 0.08, now);
        this.noise(0.055, 0.05, now, 900);
        break;
      case 'enemyShoot':
        this.tone(120, 55, 0.16, 'square', 0.045, now);
        break;
      case 'hit':
        this.noise(0.09, 0.09, now, 1300);
        this.tone(95, 55, 0.1, 'triangle', 0.05, now);
        break;
      case 'explode':
        this.noise(0.32, 0.14, now, 600);
        this.tone(82, 32, 0.32, 'sawtooth', 0.07, now);
        break;
      case 'pickup':
        this.arpeggio([440, 660, 880], 0.055, now);
        break;
      case 'shield':
        this.arpeggio([320, 480, 720, 960], 0.04, now);
        break;
      case 'wave':
        this.arpeggio([220, 330, 440], 0.08, now);
        break;
      case 'win':
        this.arpeggio([392, 523, 659, 784], 0.13, now);
        break;
      case 'lose':
        this.arpeggio([280, 220, 165], 0.16, now);
        break;
    }
  }

  /** 空间路由终端：pan≈0 直连 master，否则途经 StereoPanner（不支持时静默回落直连）。 */
  private spatialDestination(): AudioNode {
    const context = this.context!;
    const master = this.master!;
    if (Math.abs(this.spatialPan) < 0.05 || typeof context.createStereoPanner !== 'function') return master;
    const panner = context.createStereoPanner();
    panner.pan.value = this.spatialPan;
    panner.connect(master);
    return panner;
  }

  private tone(startHz: number, endHz: number, duration: number, type: OscillatorType, volume: number, time: number): void {
    if (!this.context || !this.master) return;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(startHz, time);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(20, endHz), time + duration);
    gain.gain.setValueAtTime(Math.max(0.0001, volume * this.spatialGain), time);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + duration);
    oscillator.connect(gain);
    gain.connect(this.spatialDestination());
    oscillator.start(time);
    oscillator.stop(time + duration + 0.02);
  }

  private noise(duration: number, volume: number, time: number, cutoff: number): void {
    if (!this.context || !this.master) return;
    const buffer = this.getNoiseBuffer(duration);
    if (!buffer) return;
    const source = this.context.createBufferSource();
    const filter = this.context.createBiquadFilter();
    const gain = this.context.createGain();
    filter.type = 'lowpass';
    filter.frequency.value = cutoff;
    gain.gain.setValueAtTime(Math.max(0.0001, volume * this.spatialGain), time);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + duration);
    source.buffer = buffer;
    source.connect(filter);
    filter.connect(gain);
    gain.connect(this.spatialDestination());
    source.start(time);
  }


  private getNoiseBuffer(duration: number): AudioBuffer | null {
    if (!this.context) return null;
    const key = Math.round(duration * 1000);
    const cached = this.noiseBuffers.get(key);
    if (cached) return cached;
    const length = Math.max(1, Math.floor(this.context.sampleRate * duration));
    const buffer = this.context.createBuffer(1, length, this.context.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i += 1) data[i] = (Math.random() * 2 - 1) * (1 - i / length);
    this.noiseBuffers.set(key, buffer);
    return buffer;
  }

  private arpeggio(notes: number[], spacing: number, time: number): void {
    notes.forEach((note, index) => this.tone(note, note * 1.025, spacing * 2.4, 'triangle', 0.045, time + index * spacing));
  }
}
